import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, MapPin, Smartphone } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Kicker } from "@/components/landing/Section";
import { Floater, fade } from "@/components/landing/HeroLive";
import { bySlug } from "@/lib/trolls";

const pop = (d) => ({ initial: { opacity: 0, scale: 0.8, y: 10 }, animate: { opacity: 1, scale: 1, y: 0 }, transition: { delay: d, type: "spring", stiffness: 160, damping: 14 } });

const PartnerPhoto = ({ p }) => (
  <motion.div {...fade(0.15)} className="relative lg:col-span-6">
    <div className="relative rounded-[20px] p-2.5 bg-paper border border-line shadow-journal-lg rotate-[1deg]">
      <img src="/trails/partner.webp" alt={p.title} data-testid="hero-slide-partner-image" className="w-full h-[340px] sm:h-[440px] lg:h-[560px] object-cover rounded-[14px]" />
    </div>
    <motion.div {...pop(0.5)} className="absolute -left-2 sm:-left-6 top-10 inline-flex items-center gap-2 rounded-full bg-rowan text-parchment px-4 py-2 text-sm font-extrabold shadow-journal-lg">
      <MapPin className="w-4 h-4" /> {p.pin}
    </motion.div>
    <motion.div {...pop(0.7)} className="absolute -right-2 sm:-right-5 bottom-16 inline-flex items-center gap-2 rounded-full bg-[#FF5B24] text-white px-4 py-2 text-sm font-extrabold shadow-journal-lg">
      <Smartphone className="w-4 h-4" /> Vipps
    </motion.div>
    <motion.div {...pop(0.9)} className="absolute right-6 -top-4 rotate-[8deg] rounded-full bg-amber text-parchment px-4 py-2 text-sm font-extrabold shadow-journal-lg" data-testid="hero-slide-partner-stamp">
      {p.stamp}
    </motion.div>
    <Floater troll={bySlug("kremmer-kari")} pos="-left-4 sm:-left-10 -bottom-10 h-36 sm:h-52" />
  </motion.div>
);

export default function HeroPartner() {
  const { t } = useLang();
  const p = t.trails.partner;
  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center" data-testid="hero-slide-partner">
      <div className="lg:col-span-6 lg:pr-6">
        <motion.div {...fade(0)}>
          <Kicker>{p.kicker}</Kicker>
        </motion.div>
        <motion.h1 {...fade(0.08)} data-testid="hero-slide-partner-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-ink mt-5">
          {p.title}
        </motion.h1>
        <motion.p {...fade(0.16)} className="text-base md:text-lg text-ink-muted leading-relaxed mt-6 max-w-xl">{p.pitch}</motion.p>
        <motion.ul {...fade(0.22)} className="mt-7 space-y-2.5">
          {p.points.map((pt) => (
            <li key={pt} className="flex gap-2.5 font-bold text-ink">
              <Check className="w-5 h-5 text-moss shrink-0" /> {pt}
            </li>
          ))}
        </motion.ul>
        <motion.div {...fade(0.3)} className="flex flex-wrap items-center gap-3 mt-9">
          <Link to="/supporter#signup" data-testid="hero-slide-partner-cta" className="rounded-full bg-moss hover:bg-moss-dark text-parchment px-7 py-4 text-base font-extrabold shadow-journal inline-flex items-center gap-2 transition-[background-color,transform] duration-200 hover:-translate-y-0.5">
            {p.cta} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/supporter" data-testid="hero-slide-partner-more" className="rounded-full border-2 border-ink/80 text-ink px-6 py-3.5 text-base font-extrabold inline-flex items-center gap-2 hover:bg-ink hover:text-parchment transition-colors">
            {p.cta2}
          </Link>
        </motion.div>
      </div>
      <PartnerPhoto p={p} />
    </div>
  );
}
